import Button from "components/elements/Button/Button";
import InputCheckbox from "components/elements/Input/InputCheckbox";
import Loading from "components/elements/Loading/Loading";
import FolderSvg from "components/icons/24/other/folder.svg";
import ActionRefresh from "components/icons/24/actions/action-refresh.svg";
import { getFileExtension } from "lib/actions/files";
import { useAutodeskTopFolders } from "../lib/hooks/useAutodeskTopFolders";
import { useAutodeskFolderContents } from "../lib/hooks/useAutodeskFolderContents";
import { AutodeskFolderEntryModel } from "../lib/types/AutodeskFolderEntryModel";
import AutodeskBrowserRow from "../controls/AutodeskBrowserRow";
import AutodeskFileIcon from "../controls/AutodeskFileIcon";
import AutodeskFilteredList from "../controls/AutodeskFilteredList";
import AutodeskRequestError from "../controls/AutodeskRequestError";
import { AutodeskImportStepProps } from "./AutodeskImportStepProps";

export default function AutodeskImportFolderStep(p: AutodeskImportStepProps) {
  const currentFolder = p.state.folderPath.at(-1) ?? null;
  const topFolders = useAutodeskTopFolders(
    p.state.accountId,
    p.state.hub,
    currentFolder ? null : p.state.project?.id ?? null,
  );
  const contents = useAutodeskFolderContents(
    p.state.accountId,
    p.state.hub,
    p.state.project?.id ?? null,
    currentFolder?.id ?? null,
  );
  const listing = currentFolder ? contents : topFolders;
  const entries: AutodeskFolderEntryModel[] = currentFolder
    ? contents.entries ?? []
    : (topFolders.topFolders ?? []).map((folder) => ({
        ...folder,
        isFolder: true,
        tipVersionId: null,
      }));

  if (listing.isLoading) return <Loading />;
  if (listing.hasError)
    return (
      <AutodeskRequestError
        error={listing.error}
        workspaceId={p.workspaceId}
        onRetry={() => listing.mutate()}
        onReconnected={p.onReconnected}
      />
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-thin">
          Select a <span className="font-normal">file or folder</span>
        </h1>
        <button
          type="button"
          aria-label="Refresh"
          className="hover:text-primary-600 w-6 text-icon-normal"
          onClick={() => listing.mutate()}
        >
          <ActionRefresh />
        </button>
      </div>
      <AutodeskFilteredList
        items={[
          ...entries.filter((entry) => entry.isFolder),
          ...entries.filter((entry) => !entry.isFolder),
        ]}
        placeholder="Search this folder"
        emptyText="This folder has no folders or model files"
        getKey={(entry) => entry.id}
        getName={(entry) => entry.name}
        renderItem={(entry) =>
          entry.isFolder ? (
            <AutodeskBrowserRow
              icon={<FolderSvg />}
              label={entry.name}
              onClick={() =>
                p.dispatch({
                  type: "OPEN_FOLDER",
                  folder: { id: entry.id, name: entry.name },
                })
              }
            />
          ) : (
            <AutodeskBrowserRow
              icon={
                <AutodeskFileIcon extension={getFileExtension(entry.name)} />
              }
              label={entry.name}
              onClick={() => p.dispatch({ type: "SELECT_FILE", file: entry })}
            />
          )
        }
      />
      {currentFolder && (
        <footer className="flex items-center justify-end gap-6 border-t pt-4">
          <label className="group/checkbox-item flex cursor-pointer items-center gap-2 text-sm">
            <InputCheckbox
              isChecked={p.state.shouldIncludeSubfolders}
              onChange={() => p.dispatch({ type: "TOGGLE_SUBFOLDERS" })}
            />
            Include subfolders
          </label>
          <Button
            type="button"
            slim
            onClick={() => p.dispatch({ type: "SELECT_CURRENT_FOLDER" })}
          >
            Import this folder
          </Button>
        </footer>
      )}
    </div>
  );
}
